/* eslint-disable no-shadow */
import {
  PRODUCT_IMAGES_CTA_ADD_ITEM,
  PRODUCT_IMAGES_CTA_ITEMS_LIST_RESET,
  PRODUCT_IMAGES_CTA_SET_ITEMS_ORDER,
  PRODUCT_IMAGES_CTA_REMOVE_ITEM,
  PRODUCT_IMAGES_CTA_UPDATE_ITEM,
} from '../../types/actions';

import {
  GET_PRODUCT_IMAGES_CTA_SORTED_LIST,
  GET_ALL_PRODUCT_IMAGES_CTA_DIMENSIONS,
} from '../../types/getters';

import {
  UPDATE_PRODUCT_IMAGES_CTA_ADD_ITEM,
  UPDATE_PRODUCT_IMAGES_CTA_ITEMS_ORDER,
  UPDATE_PRODUCT_IMAGES_CTA_ITEMS_LIST_RESET,
  UPDATE_PRODUCT_IMAGES_CTA_REMOVE_ITEM,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEM,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_DIMENSIONS_ENTRY,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_HEIGHT,
  UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_WIDTH,
} from '../../types/mutations';

/**
 * @typedef {Object} ItemsDimensions
 * @property {number} [height=0]
 * @property {number} [width=0]
 */

/**
 * @typedef {Object} ProductImagesCtaState
 * @property {object[]} itemsList
 * @property {string[]} itemsOrder
 * @property {Object<ItemsDimensions>} itemsDimensions
 */

/**
 * @typedef {Object} ItemData
 * @property {string=} customCssClass - a custom css class to be applied to the CTA item
 * @property {string=} customStyle - custom styles to be applied to the CTA item
 * @property {string=} id - a unique id (string) to identify the CTA item
 * @property {boolean=} isAR - a flag that determines if the product is AR eligible
 * @property {string=} label - the text to display in the CTA
 * @property {function=} onClickHandler - a function which will be called on CTA click
 * @property {boolean=} showArrow - a boolean to determine if chevron (down arrow) must be shown along text
 * @property {string | null} [url] - the url to be set as href when CTA is of <a> type
 * @property {boolean} [allowOpeningInNewTab] - a boolean to determine if url should be opened in new tab
 */

/**
 * @typedef {Object} UpdateItemPayload
 * @property {number} itemIndex - a custom css class to be applied to the CTA item
 * @property {ItemData} itemData - a unique id (string) to identify the CTA item
 */

/**
 * @typedef {Object} GetItemIndexByItemIdPayload
 * @property {string} itemId
 * @property {ProductImagesCtaState} state
 */

/**
 *
 * @param {GetItemIndexByItemIdPayload} params
 * @returns {number} - the index of the item in itemsList or -1 if NOT found
 */
const getItemIndexByItemId = ({ itemId, state }) => state.itemsList.findIndex((item) => item.id === itemId);

/**
 * @returns {ProductImagesCtaState}
 */
export function state() {
  return {
    // The list of CTA items to be displayed
    itemsList: [],
    // The reference array to sort itemsList
    itemsOrder: [],
    // CTA Button items Dimensions
    itemsDimensions: {},
  };
}

export const mutations = {

  /**
   * Adds height and width dimension entry points
   *
   * @param {ProductImagesCtaState} state
   * @param {string} key
   */
  [UPDATE_PRODUCT_IMAGES_CTA_UPDATE_DIMENSIONS_ENTRY](state, key) {
    // add entry if not cached key
    if (!state.itemsDimensions?.[key]) {
      state.itemsDimensions[key] = { height: 0, width: 0 };
    }
  },

  /**
   * Updates current height of the Product Images CTA Buttons List
   *
   * @param {ProductImagesCtaState} state
   * @param {{ key: string, height: number }} param
   */
  [UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_HEIGHT](state, { key, height }) {
    const itemsCtaDimensions = state.itemsDimensions[key];
    if (itemsCtaDimensions) {
      itemsCtaDimensions.height = height;
    }
  },

  /**
   * Updates current width of the Product Images CTA Buttons List
   *
   * @param {ProductImagesCtaState} state
   * @param {{ key: string, width: number }} param
   */
  [UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEMS_WIDTH](state, { key, width }) {
    const itemsCtaDimensions = state.itemsDimensions[key];
    if (itemsCtaDimensions) {
      itemsCtaDimensions.width = width;
    }
  },

  /**
   * Adds a new CTA item to the list.
   *
   * @param {ProductImagesCtaState} state
   * @param {ItemData} itemData
   */
  [UPDATE_PRODUCT_IMAGES_CTA_ADD_ITEM](state, itemData) {
    state.itemsList.push(itemData);
  },

  /**
   * Removes a CTA item from the list.
   *
   * @param {ProductImagesCtaState} state
   * @param {number} itemIndex
   */
  [UPDATE_PRODUCT_IMAGES_CTA_REMOVE_ITEM](state, itemIndex) {
    state.itemsList.splice(itemIndex, 1);
  },

  /**
   * Updates a CTA item from the list.
   *
   * @param {ProductImagesCtaState} state
   * @param {UpdateItemPayload} payload
   */
  [UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEM](state, { itemIndex, itemData }) {
    // Exit if required variables are not set
    if (!Number.isInteger(itemIndex) || !itemData) return;

    /**
     * Iterate over each passed property/value to update
     * itemData properties
     */
    Object.entries(itemData).forEach(
      ([key, val]) => {
        state.itemsList[itemIndex][key] = val;
      },
    );
  },

  /**
   * Reset the items list array.
   *
   * @param {ProductImagesCtaState} state
   */
  [UPDATE_PRODUCT_IMAGES_CTA_ITEMS_LIST_RESET](state) {
    state.itemsList = [];
  },

  /**
   * Sets the array to be used as reference to order the
   * CTA items. The values must be a string with the id of
   * each items.
   *
   * @param {ProductImagesCtaState} state
   * @param {string[]} ids
   */
  [UPDATE_PRODUCT_IMAGES_CTA_ITEMS_ORDER](state, ids) {
    state.itemsOrder = ids;
  },

};

export const getters = {

  /**
   * Gets itemsList sorted based on itemsOrder
   * @param {ProductImagesCtaState} state
   */
  [GET_PRODUCT_IMAGES_CTA_SORTED_LIST](state) {
    /**
     * @param {object} a
     * @param {object} b
     * @returns {number}
     */
    const sortCallback = (a, b) => {
      /**
       * Move items NOT included in itemsOrder to after
       * the items included in itemsOrder
      */
      if (!state.itemsOrder.includes(a.id)) return 1;
      if (!state.itemsOrder.includes(b.id)) return -1;
      return state.itemsOrder.indexOf(a.id) - state.itemsOrder.indexOf(b.id);
    };

    const originalArray = state.itemsList.slice();

    return originalArray.sort(sortCallback);
  },

  /**
   * Gets all Product Images CTA Buttons List dimensions
   *
   * @param {ProductImagesCtaState} state
   * @returns {ItemsDimensions}
   */
  [GET_ALL_PRODUCT_IMAGES_CTA_DIMENSIONS](state) {
    return state.itemsDimensions;
  },

};

export const actions = {

  /**
   * Adds a new item to the list and sort the list
   * @param {import('vuex').ActionContext<ProductImagesCtaState, Object>} context
   * @param {ItemData} itemData
   */
  [PRODUCT_IMAGES_CTA_ADD_ITEM]({ commit }, itemData) {
    commit(UPDATE_PRODUCT_IMAGES_CTA_ADD_ITEM, itemData);
  },

  /**
   * Reset (empty) itemsList
   * @param {import('vuex').ActionContext<ProductImagesCtaState, Object>} context
   */
  [PRODUCT_IMAGES_CTA_ITEMS_LIST_RESET]({ commit }) {
    commit(UPDATE_PRODUCT_IMAGES_CTA_ITEMS_LIST_RESET);
  },

  /**
   * Sets the items order array and sort the list accordingly
   * @param {import('vuex').ActionContext<ProductImagesCtaState, Object>} context
   * @param {string[]} ids
   */
  [PRODUCT_IMAGES_CTA_SET_ITEMS_ORDER]({ commit }, ids) {
    commit(UPDATE_PRODUCT_IMAGES_CTA_ITEMS_ORDER, ids);
  },

  /**
   * Remove an item from the list
   * @param {import('vuex').ActionContext<ProductImagesCtaState, Object>} context
   * @param {string} itemId
   */
  [PRODUCT_IMAGES_CTA_REMOVE_ITEM]({ commit, state }, itemId) {
    const itemIndex = getItemIndexByItemId({ itemId, state });
    // Commit only if item was found
    if (itemIndex > -1) {
      commit(UPDATE_PRODUCT_IMAGES_CTA_REMOVE_ITEM, itemIndex);
    }
  },

  /**
   * Update an item from the list
   * @param {import('vuex').ActionContext<ProductImagesCtaState, Object>} context
   * @param {{ itemData: ItemData, itemId: string }} param
   */
  [PRODUCT_IMAGES_CTA_UPDATE_ITEM]({ commit, state }, param) {
    const itemIndex = getItemIndexByItemId({ itemId: param.itemId, state });
    // Commit only if item was found
    if (itemIndex > -1) {
      commit(UPDATE_PRODUCT_IMAGES_CTA_UPDATE_ITEM, {
        itemData: param.itemData,
        itemIndex,
      });
    }
  },

};
